import { Heading, VStack, Input, Button, Box, Table, Thead, 
    Tbody, Tr, Th, Td, Select, useToast, Checkbox, Text, 
    useColorModeValue,
    IconButton, HStack } from '@chakra-ui/react';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tooltip } from "@chakra-ui/react";
import { jwtDecode } from "jwt-decode";
import { format } from 'date-fns';
import { useNavigate } from "react-router-dom";
import { FiEdit, FiCheck, FiX, FiTrash } from "react-icons/fi";
import { Domain, Category } from '../../Platform/Common/types';
import { useParams } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";

// Define the expected structure of the token payload
interface TokenPayload {
  sub: string;       // Typically the username or user ID
  role_code: string;   // Role ID
  exp?: number;      // Expiration timestamp (optional)
}

type OperationMode = "none" | "resequence" ;


const B2CDomainManager = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const navigate = useNavigate();

  const toast = useToast();
  const tableHeaderbg = useColorModeValue("gray.200", "gray.700");

  const [categories, setCategories] = useState<Category[]>([]);

  const { category_id } = useParams<{ category_id: string }>();

   const [operationMode, setOperationMode] = useState<OperationMode>("none");

   const [needsResequence, setNeedsResequence] = useState(false);


  // Fetch enrolled students when tenant ID is set
const fetchDomains = useCallback(async () => {
  try {
    const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
    const response = await axios.get(`${b2c_apiurl}/category/${category_id}/domains`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('b2c_token')}`,
      },
    });
    setDomains(response.data);
  } catch (error) {
    console.error("Failed to load B2C Domains", error);
  }
}, []);

const fetchCategories = useCallback(async () => {
  try {
    const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
    const response = await axios.get(`${b2c_apiurl}/categories`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('b2c_token')}`,
      },
    });
    setCategories(response.data);
  } catch (error) {
    console.error("Failed to load B2C Categories", error);
  }
}, []);

useEffect(() => {
  fetchDomains();
  fetchCategories();
}, [fetchDomains, fetchCategories]);

useEffect(() => {
  if (needsResequence) {
    handleSequenceCommit();
    setNeedsResequence(false);
  }
}, [domains, needsResequence]);

  const handleDeleteDomain = async (domain_id: number) => {
    // Placeholder function
       try {
         const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
       console.log("Printing Token from Delete" + localStorage.getItem("b2c_token"));
       await axios.delete(apiurl + `/domain/${domain_id}`, {
         headers: {
           Authorization: `Bearer ${localStorage.getItem('b2c_token')}`
         }
       });
       await fetchDomains();

       setNeedsResequence(true);   // trigger resequence
       toast({ title: "B2C Domain deleted successfully", status: "success" });
     } catch (err: any) {
    console.error(err);

        const message =
          err?.response?.data?.detail ||
          "Error deleting domain";

        toast({
          title: message,
          status: "error",
        });
      }
    };

  const handleAddNewDomain = () => {

    const domain_id = 0;
    navigate(`/practice/app/b2c-category/${category_id}/review-domain/${domain_id}`);

  };

        const handleMoveUp = (index: number) => {
          if (index === 0 || operationMode !== "resequence") return;
          const updated = [...domains];
          [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
          setDomains(updated);
        };
        const handleMoveDown = (index: number) => {
          if (index === categories.length - 1 || operationMode !== "resequence") return;
          const updated = [...domains];
          [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
          setDomains(updated);
        };
      
        // Commit handler:  resequence 
        const handleSequenceCommit = async () => {
          const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
          const token = localStorage.getItem("b2c_token");
          try {
  
              // Resequence: compute new order from
              const updatedSequence = domains.map((dm, i) => ({
                domain_id: dm.domain_id,
                sequence_number: i + 1,
              }));
              console.log("printing domain resequence order",updatedSequence);
              await axios.put(`${apiurl}/category/${category_id}/domains/reorder`, updatedSequence, {
                headers: { Authorization: `Bearer ${token}` },
              });
              toast({
                title: "New order committed",
                status: "success",
                duration: 3000,
                isClosable: true,
              });
      
            // Refresh data
            await fetchDomains();
            setOperationMode("none");
  
          } catch (error) {
            toast({
              title: `Failed to ${operationMode} questions`,
              status: "error",
              duration: 3000,
              isClosable: true,
            });
          }
        };
          const handleReviewOffers = (domain_id: number) => {
            navigate(`/practice/app/b2c-category/${category_id}/domain/${domain_id}/offers`);
        };
    const handleCancelOperation = () => {
                fetchDomains();
            setOperationMode("none");
            }

    const handleReviewDomain = (domain_id: number) => {
      navigate(`/practice/app/b2c-category/${category_id}/review-domain/${domain_id}`);
    };


  return (
    <Box as="main" mx="auto" p={10} >
      <VStack>
        <Heading size={"md"}>B2C Domain Manager</Heading>
      </VStack>
            <Box mt={4}>
               <Text fontWeight="bold">Existing B2C Domains</Text>

                  <HStack mt={2}>
                    <Button   borderRadius={"sm"} fontSize="sm" fontWeight="normal" onClick={handleAddNewDomain} isDisabled={operationMode === "resequence"}>
                      + B2C Domain
                    </Button>
                    <Button
                      borderRadius={"sm"} fontSize="sm" fontWeight="normal"
                      onClick={() => {
                        setOperationMode("resequence");
                      }}
                      isDisabled={operationMode === "resequence"}
                    >
                      Resequence Domains
                    </Button>
        
                  </HStack>
        
                  {(operationMode !== "none") && (
                    <HStack mt={4} spacing={3}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleSequenceCommit}
                  >
                    Commit
                  </Button>
                    <Button size={"sm"} variant="outline" colorScheme="red" onClick={handleCancelOperation}>
                      Cancel
                    </Button>
                    </HStack>
                  )}
      </Box>
      <Box mt={4}>
       
        <Table mt={4} borderColor={"gray.300"} borderWidth={1} borderRadius="sm">
          <Thead fontSize="12">
            <Tr bgColor={tableHeaderbg}>
              <Th>ID</Th>
            <Th>Sequence</Th>
              <Th>Domain Name</Th>
              <Th>Domain Slug</Th>
              <Th>Category</Th>
              <Th>Description</Th>
{/*               <Th>Hero Text</Th>

            <Th>Hero Headline</Th>
            <Th>Promo Headline </Th>
            <Th>Promo Text</Th>
            <Th>Promo Active</Th>
              
             <Th>Meta Title</Th>
             <Th>Meta Description</Th> */}

              <Th>Status</Th>
              {/* <Th>Created At</Th>
              <Th>Updated At</Th> */}
             
            <Th>Actions</Th>
            <Th>Offers</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="12">

            {domains.map((domain,index) => (
                <Tr
                key={domain.domain_id}
          
                >
                <Td>{domain.domain_id}</Td>
                <Td>{domain.sequence}</Td>
                <Td> {domain.name}</Td>
                <Td>{ domain.slug}</Td>

                <Td>
                      {  categories.find((c) => c.category_id === domain.category_id)
                            ? `${categories.find((c) => c.category_id === domain.category_id)?.name} - ${categories.find((c) => c.category_id === domain.category_id)?.slug}`
                            : "Uncategorized"
                      }
                </Td>

                <Td>{domain.description}</Td>
{/*                 <Td>{domain.hero_text}</Td>
                <Td>{domain.hero_headline}</Td>
                <Td>{domain.promo_headline}</Td>
                <Td>{domain.promo_text}</Td>
                <Td>
                    { domain.promo_active ? (
                        "Active"
                        ) : (
                        "Inactive"
                        )
                    }
                </Td>
                <Td>{domain.meta_description}</Td>
                <Td>{domain.meta_title}</Td> */}
                <Td>
                    {domain.is_active ? (
                        "Active"
                        ) : (
                        "Inactive")
                        }
                </Td>

                <Td>
                    {operationMode === "resequence" && (
                    <>
                        <IconButton
                        size="xs"
                        icon={<ChevronUpIcon />}
                        aria-label="Move Up"
                        onClick={e => { e.stopPropagation(); handleMoveUp(index); }}
                        />
                        <IconButton
                        size="xs"
                        icon={<ChevronDownIcon />}
                        aria-label="Move Down"
                        onClick={e => { e.stopPropagation(); handleMoveDown(index); }}
                        />
                    </>
                    )}
                {operationMode === "none" && (
                    <>
                    <Tooltip label="Edit">
                        <Button size="sm" onClick={() => handleReviewDomain(domain.domain_id)} mr={8}>
                        <FiEdit />
                        </Button>
                    </Tooltip>
                    <Tooltip label="Delete">
                    <Button
                            size="sm"
                            colorScheme="red"
                            onClick={() => {
                            if (window.confirm("Are you sure you want to delete this category?")) {
                                handleDeleteDomain(domain.domain_id);
                            }
                            }}
                        >
                            <FiTrash />
                        </Button>
                    </Tooltip>
                    </>
                    )}

                </Td>
                <Td>
                    <Tooltip label="Review Domains for the category">
                    <Button size="sm" onClick={() => handleReviewOffers(domain.domain_id)}>
                        Offers
                    </Button>
                    </Tooltip>
                </Td>

                </Tr>
            ))}

            </Tbody>

        </Table>
      </Box>

    </Box>
  );
};

export default B2CDomainManager;
